import React, { useMemo, useRef } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { colors } from "../../config/theme";
import { toQuery } from "../../helpers/general";
import { useCookie } from "../../hooks/useCookie";
import apiService from "../../service/apiService";

const ACCENT = "#f0813f";

type Variant = "default" | "pill";

/** Style overrides that only apply to the compact filter look */
const pillStyles = {
  control: (base: any, state: any) => ({
    height: 38,
    minHeight: 38,
    borderRadius: 999,
    fontSize: 13,
    backgroundColor: "#fff",
    borderColor: state.isFocused ? ACCENT : "#e5e5e5",
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? ACCENT : "#d0d0d0" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px 0 14px" }),
  placeholder: (base: any) => ({ ...base, color: "#777" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "0 10px" }),
  clearIndicator: (base: any) => ({ ...base, padding: "0 4px" }),
  menu: (base: any) => ({ borderRadius: 12, overflow: "hidden", fontSize: 13 }),
  option: (base: any, state: any) => ({
    backgroundColor: state.isSelected
      ? ACCENT
      : state.isFocused
        ? "rgba(240,129,63,0.1)"
        : "transparent",
    color: state.isSelected ? "#fff" : "#222",
  }),
};

const InfiniteScrollSelect = React.forwardRef((props: any, ref) => {
  const {
    url,
    isSearchable = true,
    isLoading = false,
    defaultValue = "",
    onChange = null,
    onSelect,
    placeholder = "Choisissez une option",
    value: controlledValue,
    variant = "default" as Variant,
    ...rest
  } = props;

  const { token } = useCookie("vToken");
  const selectRef = useRef<any>(null);

  async function loadOptions(
    search: string,
    _loadedOptions: any[],
    additional: any,
  ) {
    const page = additional?.page || 1;
    const endpoint = `${url.path}${toQuery({ page, limit: 10, q: search })}`;

    const response = await apiService.MakeGetRequest(endpoint, token);

    const items = Array.isArray(response) ? response : response?.items || [];
    const totalPages = response?.meta?.totalPages || 1;

    return {
      options: items,
      hasMore: page < totalPages,
      additional: { page: page + 1 },
    };
  }

  // Built once per variant instead of on every render
  const styles = useMemo(() => {
    const isPill = variant === "pill";
    const extra = isPill ? pillStyles : {};

    const merge = (key: string, base: any) => (b: any, s: any) => ({
      ...base(b, s),
      ...((extra as any)[key]?.(b, s) ?? {}),
    });

    return {
      control: merge("control", (b: any, s: any) => ({
        ...b,
        boxShadow: s.isFocused
          ? "rgb(129, 227, 249) 0rem 0rem 0rem 0.125rem"
          : "none",
        minWidth: 0,
      })),
      valueContainer: merge("valueContainer", (b: any) => ({
        ...b,
        overflow: "hidden",
        flexWrap: "nowrap",
        maxWidth: "100%",
      })),
      input: (b: any) => ({
        ...b,
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: "0",
      }),
      singleValue: (b: any) => ({
        ...b,
        maxWidth: "calc(100% - 20px)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),
      placeholder: merge("placeholder", (b: any) => ({
        ...b,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "100%",
      })),
      indicatorSeparator: merge("indicatorSeparator", (b: any) => b),
      dropdownIndicator: merge("dropdownIndicator", (b: any) => b),
      clearIndicator: merge("clearIndicator", (b: any) => b),
      // Menu and options may expand and wrap, they are not truncated
      menu: merge("menu", (b: any) => ({
        ...b,
        minWidth: "100%",
        width: "max-content",
        maxWidth: "400px",
        zIndex: 9999,
      })),
      option: merge("option", (b: any) => ({
        ...b,
        whiteSpace: "normal",
        overflow: "visible",
        wordBreak: "break-word",
      })),
      // The menu is rendered in a portal, so it needs its own z-index
      menuPortal: (b: any) => ({ ...b, zIndex: 9999 }),
    };
  }, [variant]);

  return (
    <AsyncPaginate
      value={
        controlledValue !== undefined ? controlledValue : defaultValue || null
      }
      loadOptions={loadOptions}
      onChange={(option: any) => {
        onChange && onChange(option);
        onSelect && onSelect(option);
      }}
      placeholder={placeholder}
      isLoading={isLoading}
      isSearchable={isSearchable}
      isMulti={false}
      maxMenuHeight="200px"
      minMenuHeight="200px"
      loadingMessage={() => <p>Chargement...</p>}
      noOptionsMessage={() => "Aucun résultat"}
      filterOption={(option: any, searchValue: string) => {
        const value = option?.label || option?.data?.name;
        return value?.toLowerCase().includes(searchValue?.toLowerCase());
      }}
      theme={(theme) => ({
        ...theme,
        colors: { ...theme.colors, primary: colors.primary },
      })}
      styles={styles}
      menuPosition="absolute"
      menuPortalTarget={document.body}
      cacheUniqs={[url.path]}
      selectRef={selectRef}
      defaultInputValue={defaultValue?.title ?? ""}
      {...rest}
    />
  );
});

export default InfiniteScrollSelect;
